import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-breadcrumb',
  imports: [RouterModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (breadcrumbs().length > 0) {
      <div class="c-breadcrumb">
        @for (crumb of breadcrumbs(); track crumb.url; let last = $last) {
          <div class="c-breadcrumb__item">
            @if (!last) {
              <a [routerLink]="crumb.url" class="c-breadcrumb__link">{{ crumb.label }}</a>
            }
            @if (last) {
              <span class="c-breadcrumb__current">{{ crumb.label }}</span>
            }
            @if (!last) {
              <mat-icon class="c-breadcrumb__separator">chevron_right</mat-icon>
            }
          </div>
        }
      </div>
    }
  `,
  styles: [
    `
      /* Stays visible while the page scrolls. */
      :host {
        display: block;
        position: sticky;
        top: 0;
        z-index: 20;
        background: var(--surface-bg);
      }

      :host:has(.c-breadcrumb) {
        border-bottom: 1px solid var(--border-default, rgba(148, 163, 184, 0.18));
      }

      .c-breadcrumb {
        display: flex;
        align-items: center;
        max-width: 1680px;
        width: 100%;
        margin: 0 auto;
        padding: 12px 28px;
        box-sizing: border-box;
        font-size: 0.82rem;
        font-weight: 500;
        color: var(--text-secondary, #475569);

        &__item {
          display: flex;
          align-items: center;
        }

        &__link {
          color: var(--text-secondary, #475569);
          text-decoration: none;
          transition: color 0.2s ease;
          &:hover {
            color: var(--gold, #c8a96b);
          }
        }

        &__current {
          color: var(--text-primary, #121212);
          font-weight: 700;
        }

        &__separator {
          font-size: 1.05rem;
          width: 1.05rem;
          height: 1.05rem;
          margin: 0 6px;
          color: var(--text-muted, #94a3b8);
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      @media (max-width: 768px) {
        /* The floating menu button sits in the top-left corner on mobile. */
        :host {
          position: static;
        }

        .c-breadcrumb {
          padding: 10px 14px 0 14px;
        }
      }
    `,
  ],
})
export class BreadcrumbComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  readonly breadcrumbs = signal<Array<{ label: string; url: string }>>([]);

  private readonly routeLabels: Record<string, string> = {
    home: 'Home',
    'schedule-planning': 'Planejamento de Escala',
    branches: 'Operações & Regras',
    employees: 'Funcionários & Férias',
    'demand-history': 'Demanda & Feriados',
    timecard: 'Ponto & Chamados',
    reports: 'Relatórios & BI',
    admin: 'Administração & Logs',
  };

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.breadcrumbs.set(this.buildBreadcrumbs(this.router.url));
      });
    this.breadcrumbs.set(this.buildBreadcrumbs(this.router.url));
  }

  private buildBreadcrumbs(url: string): Array<{ label: string; url: string }> {
    const segments = url
      .split('?')[0]
      .split('/')
      .filter((segment) => segment);

    if (segments.length === 0 || segments[0] === 'home' || segments[0] === 'login') {
      return [];
    }

    const breadcrumbs = [{ label: 'Home', url: '/home' }];
    let currentUrl = '';

    segments.forEach((segment) => {
      currentUrl += `/${segment}`;
      breadcrumbs.push({
        label: this.routeLabels[segment] || segment,
        url: currentUrl,
      });
    });

    return breadcrumbs;
  }
}
